import { Stack, Text } from '@mantine/core'
import { IconMapPin } from '@tabler/icons-react'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	CommunityLocationScope,
	CommunityLocationStat,
	getCommunityCmsLocationStats,
} from 'src/apis/communityAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import TableComponent from 'src/components/TableComponent'
import { useModal } from 'src/context/ModalContext'
import { getNationalityDisplay } from 'src/pages/ActiveUsersPage/activeUserDisplay'

interface CommunityLocationStatsPageProps {
	scope: CommunityLocationScope
}

const getLocationLabel = (scope: CommunityLocationScope, item: CommunityLocationStat) => {
	if (scope === 'vietnam') return item.location_label
	return getNationalityDisplay('', item.location_label).name === '-'
		? item.location_label
		: getNationalityDisplay('', item.location_label).name
}

function CommunityLocationStatsPage({ scope }: CommunityLocationStatsPageProps) {
	const { openError } = useModal()
	const [, setSearchParams] = useSearchParams()
	const [items, setItems] = useState<CommunityLocationStat[]>([])
	const [loading, setLoading] = useState(false)
	const [searchKey, setSearchKey] = useState('')
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})

	const fetchItems = useCallback(async () => {
		setLoading(true)
		try {
			const response = await getCommunityCmsLocationStats(scope)
			setItems(response?.data?.results?.object || [])
		} catch (error) {
			setItems([])
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [openError, scope])

	useEffect(() => {
		fetchItems()
	}, [fetchItems])

	const filteredItems = useMemo(() => {
		const normalizedSearch = searchKey.trim().toLowerCase()
		if (!normalizedSearch) return items

		return items.filter((item) =>
			getLocationLabel(scope, item).toLowerCase().includes(normalizedSearch),
		)
	}, [items, scope, searchKey])

	const rows = useMemo(
		() =>
			filteredItems
				.slice(
					(pagination.pageIndex - 1) * pagination.pageSize,
					pagination.pageIndex * pagination.pageSize,
				)
				.map((item, index) => ({
					...item,
					no:
						index + 1 + (pagination.pageIndex - 1) * pagination.pageSize,
					location: getLocationLabel(scope, item),
				})),
		[filteredItems, pagination, scope],
	)

	const openCommunityList = useCallback(
		(item: CommunityLocationStat) => {
			setSearchParams((previous) => {
				const next = new URLSearchParams(previous)
				next.set('tab', 'communities')
				next.set('page', '1')
				next.delete('location_page')
				next.delete('location_limit')
				next.delete('location_search')
				next.delete('search_key')

				if (scope === 'vietnam') {
					next.set('location', 'vietnam')
					next.set('province', item.location_label)
					next.delete('country')
				} else {
					next.set('country', item.location_key)
					next.delete('location')
					next.delete('province')
				}

				return next
			}, { replace: true })
		},
		[scope, setSearchParams],
	)

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{ accessorKey: 'no', header: 'No.', size: 70 },
			{
				accessorKey: 'location',
				header: 'Location',
				Cell: ({ row }: any) => (
					<button
						type="button"
						onClick={() => openCommunityList(row.original)}
						style={{
							alignItems: 'center',
							background: 'none',
							border: 0,
							color: '#006B35',
							cursor: 'pointer',
							display: 'inline-flex',
							fontWeight: 700,
							gap: 6,
							padding: 0,
						}}
					>
						<IconMapPin size={16} />
						{row.original.location}
					</button>
				),
			},
			{
				accessorKey: 'community_count',
				header: 'Communities',
				size: 180,
				Cell: ({ cell }: any) =>
					Number(cell.getValue() || 0).toLocaleString('en-US'),
			},
		],
		[openCommunityList],
	)

	const title = scope === 'vietnam' ? 'Communities in Vietnam' : 'Communities in Other Countries'
	const description = scope === 'vietnam'
		? 'View totals by province or city and open the filtered community list.'
		: 'View totals by country and open the filtered community list.'

	return (
		<Stack spacing={16}>
			<Stack spacing={4}>
				<Text size={24} fw={800} color="#111827">{title}</Text>
				<Text size="sm" color="dimmed">{description}</Text>
			</Stack>
			<CustomClearInput
				value={searchKey}
				onChange={(value) => {
					setSearchKey(value)
					setPagination((previous) => ({ ...previous, pageIndex: 1 }))
				}}
				placeholder={scope === 'vietnam' ? 'Search province or city' : 'Search country'}
				sx={{ maxWidth: 380 }}
			/>
			<TableComponent
				columns={columns}
				total={filteredItems.length}
				data={rows}
				pagination={pagination}
				loading={loading}
				handleChangePage={(pageIndex: number) =>
					setPagination((previous) => ({ ...previous, pageIndex }))
				}
				handleChangePageSize={(pageSize: number) =>
					setPagination({ pageIndex: 1, pageSize })
				}
				enableRowActions={false}
				syncQueryUrl={false}
				compact
			/>
		</Stack>
	)
}

export default CommunityLocationStatsPage
